import { IconCalendar } from "@dream/ui";
import type { CalendarSource } from "../router.tsx";

/**
 * Faixa acima da grade quando o período (ou o filtro) não tem entregas.
 *
 * A grade continua visível: no painel a equipe precisa clicar/arrastar para
 * criar; no portal o cliente precisa ver que está num calendário, e não numa
 * tela vazia genérica.
 */

export function CalendarEmptyHint({
  readOnly,
  filtered,
  needsSetup = false,
  overlayOnly = false,
  fonte = "todas",
  onCreate,
  sliceLabel,
  nextJumpLabel,
  onGoToNext,
}: {
  readOnly: boolean;
  filtered: boolean;
  needsSetup?: boolean;
  overlayOnly?: boolean;
  fonte?: CalendarSource;
  onCreate?: () => void;
  sliceLabel?: string | null;
  nextJumpLabel?: string | null;
  onGoToNext?: () => void;
}) {
  const namedSlice = sliceLabel !== null && sliceLabel !== undefined && sliceLabel !== "";
  const title = overlayOnly
    ? "Só a Agenda neste período"
    : needsSetup
      ? "Nada para mostrar ainda"
      : fonte === "agenda"
        ? "Nenhum compromisso da Agenda neste período"
        : "Nenhuma entrega neste período";
  const description = overlayOnly
    ? "As entregas deste recorte estão vazias, mas a Google Agenda aparece na grade. Crie uma entrega ou mude o filtro Tudo / Entregas / Agenda."
    : filtered
      ? fonte === "agenda"
        ? "Nenhum compromisso da Agenda neste recorte. Volte para Tudo ou mude o período."
        : namedSlice
          ? `${sliceLabel} não tem entregas neste período. ${
              nextJumpLabel !== null && nextJumpLabel !== undefined
                ? `A próxima está em ${nextJumpLabel}.`
                : "Ajuste o recorte ou mude o período."
            }`
          : "Nenhuma entrega corresponde aos filtros aplicados. Ajuste ou limpe os filtros."
      : needsSetup
        ? "Cadastre um cliente e depois use Nova entrega — ou arraste um intervalo na grade."
        : readOnly
          ? "Navegue com as setas. Quando a Dream Vision lançar uma entrega visível, ela aparece aqui sozinha."
          : fonte === "agenda"
            ? "Conecte a Agenda em Configurações ou mude o período. Entregas voltam no recorte Tudo."
            : "Selecione um intervalo na grade, pressione N ou use Nova entrega.";

  return (
    <div
      role="status"
      className="dream-enter flex shrink-0 items-start gap-3 border-b border-[var(--color-line)] bg-[var(--color-surface-subtle)] px-3 py-2.5 sm:px-4 sm:py-3 lg:px-5"
    >
      <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg bg-[var(--dream-info-bg)] text-[var(--dream-info-fg)]">
        <IconCalendar className="size-4" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[0.8125rem] font-bold tracking-[0] text-[var(--color-ink)]">{title}</p>
        <p className="mt-0.5 text-[0.75rem] leading-5 text-[var(--color-ink-secondary)]">
          {description}
        </p>
        {onGoToNext !== undefined && nextJumpLabel !== null && nextJumpLabel !== undefined && (
          <button
            type="button"
            onClick={onGoToNext}
            className="mt-1.5 mr-3 text-[0.75rem] font-semibold text-brand-600 hover:underline"
          >
            Ir para {nextJumpLabel}
          </button>
        )}
        {!readOnly && !filtered && !overlayOnly && onCreate !== undefined && fonte !== "agenda" && (
          <button
            type="button"
            onClick={onCreate}
            className="mt-1.5 text-[0.75rem] font-semibold text-brand-600 hover:underline"
          >
            {needsSetup ? "Começar pelo cadastro" : "Nova entrega neste período"}
          </button>
        )}
      </div>
    </div>
  );
}
